/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-floating-promises */
import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import {
  Button,
  Card,
  DatePicker,
  Message,
  Radio,
  Space,
  Table,
  TableColumnProps,
  Typography
} from '@arco-design/web-react';
import * as Icon from '@arco-design/web-react/icon';
import * as echarts from 'echarts';
import dayjs from 'dayjs';
import { get } from '../requestNew';
import './statistics.scss';

const RadioGroup = Radio.Group;
const Text = Typography.Text;

// 时间维度选项
const timeDimensionOptions = [
  { label: '日', value: 'day' },
  { label: '周', value: 'week' },
  { label: '月', value: 'month' },
  { label: '年', value: 'year' }
];

interface DayNotebookStatistics {
  date: string;
  totalNum: number;
  recommendNum: number;
  topicNum: number;
  orderNum: number;
}

interface NotebookStatisticsResp {
  noteTotalNum: number;
  noteRecommendNum: number;
  noteTopicNum: number;
  noteOrderNum: number;
  noteTotalNumPre: number;
  noteRecommendNumPre: number;
  noteTopicNumPre: number;
  noteOrderNumPre: number;
  totalRate: string;
  recommendRate: string;
  topicRate: string;
  orderRate: string;
  daysDataList: DayNotebookStatistics[];
}

// 计算默认日期：周一或周二返回上一个周日，周三或以后返回当天
const getDefaultDate = (): string => {
  const today = dayjs();
  const dayOfWeek = today.day(); // 0=周日, 1=周一, 2=周二, ..., 6=周六
  
  // 如果是周一(1)或周二(2)，返回上一个周日
  if (dayOfWeek === 1 || dayOfWeek === 2) {
    // 计算上一个周日：如果今天是周一，减去1天；如果是周二，减去2天
    const lastSunday = today.subtract(dayOfWeek, 'day');
    return lastSunday.format('YYYY-MM-DD');
  }
  
  // 如果是周三或以后，返回当天
  return today.format('YYYY-MM-DD');
};

function NoteStatistics() {
  const [timeDimension, setTimeDimension] = useState<string>('week');
  const [queryDate, setQueryDate] = useState<string>(getDefaultDate());
  const [loading, setLoading] = useState(false);
  const [statisticsData, setStatisticsData] = useState<NotebookStatisticsResp | null>(null);

  // 表格分页相关
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);

  // 获取统计数据
  const fetchStatistics = (dimension?: string) => {
    const currentDimension = dimension || timeDimension;
    if (!currentDimension) {
      Message.warning('请选择时间维度');
      return;
    }

    setLoading(true);
    const params: any = { timeDimension: currentDimension };
    if (queryDate) {
      params.queryDate = queryDate;
    }

    get<NotebookStatisticsResp>('/notebook/statistics', params)
      .then((res: any) => {
        if (res && res.code === 200 && res.data) {
          setStatisticsData(res.data);
        } else {
          Message.error(res?.message || '获取统计数据失败');
        }
      })
      .catch((err: any) => {
        console.error('获取统计数据失败:', err);
        Message.error('获取统计数据失败');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  function renderBarChart(container: HTMLDivElement | null, chartData: { dates: string[]; series: any[] }) {
    if (!container) return null;

    const chart = echarts.init(container);
    
    // 获取报单笔记的索引，用于设置默认不选中
    const orderNoteIndex = chartData.series.findIndex(s => s.name === '报单笔记数');
    const selectedMap: Record<string, boolean> = {};
    chartData.series.forEach((s, index) => {
      // 报单笔记默认不选中
      selectedMap[s.name] = index !== orderNoteIndex;
    });
    
    const option: echarts.EChartsOption = {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross'
        }
      },
      legend: {
        data: chartData.series.map(s => s.name),
        top: 10,
        selected: selectedMap
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: chartData.dates
      },
      yAxis: {
        type: 'value'
      },
      series: chartData.series,
      animation: true,
      animationDuration: 300
    };

    chart.setOption(option);
    
    // 响应式调整
    const handleResize = () => {
      chart.resize();
    };
    window.addEventListener('resize', handleResize);
    
    // 将 resize 处理函数保存到 chart 实例上，以便后续清理
    (chart as any).__resizeHandler = handleResize;

    return chart;
  }

  function updateBarChart(chart: echarts.ECharts, chartData: { dates: string[]; series: any[] }) {
    if (!chart || !chartData || !chartData.dates || !chartData.series) {
      return;
    }

    // 验证数据
    if (chartData.dates.length === 0 || chartData.series.length === 0) {
      return;
    }

    // 获取报单笔记的索引，用于设置默认不选中
    const orderNoteIndex = chartData.series.findIndex(s => s.name === '报单笔记数');
    const selectedMap: Record<string, boolean> = {};
    chartData.series.forEach((s, index) => {
      // 报单笔记默认不选中
      selectedMap[s.name] = index !== orderNoteIndex;
    });

    // 使用与初始化时相同的完整配置结构
    const option: echarts.EChartsOption = {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross'
        }
      },
      legend: {
        data: chartData.series.map(s => s.name),
        top: 10,
        selected: selectedMap
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: chartData.dates
      },
      yAxis: {
        type: 'value'
      },
      series: chartData.series,
      animation: true,
      animationDuration: 300
    };
    
    // 直接使用 setOption 更新，ECharts 会自动处理数据更新
    // 使用 notMerge: true 来完全替换配置
    chart.setOption(option, true);
  }

  // 监听时间维度和日期变化，自动触发请求
  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      return;
    }
    // 时间维度或日期变化时自动触发请求
    fetchStatistics();
  }, [timeDimension, queryDate]);

  // 监听统计数据变化，重新渲染图表
  useEffect(() => {
    // 如果是日维度，不渲染折线图
    if (timeDimension === 'day') {
      // 清空已有的图表实例
      if (chartInstanceRef.current) {
        const resizeHandler = (chartInstanceRef.current as any).__resizeHandler;
        if (resizeHandler) {
          window.removeEventListener('resize', resizeHandler);
        }
        chartInstanceRef.current.dispose();
        chartInstanceRef.current = null;
      }
      // 清空容器 DOM
      if (chartContainerRef.current) {
        chartContainerRef.current.innerHTML = '';
      }
      return;
    }

    if (!statisticsData || !statisticsData.daysDataList || statisticsData.daysDataList.length === 0) {
      return;
    }

    // 确保容器存在
    if (!chartContainerRef.current) {
      return;
    }

    // 彻底销毁旧的图表实例
    if (chartInstanceRef.current) {
      const resizeHandler = (chartInstanceRef.current as any).__resizeHandler;
      if (resizeHandler) {
        window.removeEventListener('resize', resizeHandler);
      }
      chartInstanceRef.current.dispose();
      chartInstanceRef.current = null;
    }

    // 清空容器 DOM
    if (chartContainerRef.current) {
      chartContainerRef.current.innerHTML = '';
    }

    // 转换数据并重新创建图表
    const chartData = chartDataTrans(statisticsData.daysDataList);
    chartInstanceRef.current = renderBarChart(chartContainerRef.current, chartData);
  }, [statisticsData, timeDimension]);

  // 组件卸载时清理图表实例
  useEffect(() => {
    // 页面加载时获取数据
    fetchStatistics();
    return () => {
      if (chartInstanceRef.current) {
        // 移除 resize 事件监听器
        const resizeHandler = (chartInstanceRef.current as any).__resizeHandler;
        if (resizeHandler) {
          window.removeEventListener('resize', resizeHandler);
        }
        // 销毁图表实例
        chartInstanceRef.current.dispose();
        chartInstanceRef.current = null;
      }
    };
  }, []);

  // 格式化数字，添加千分位
  const formatNumber = (num: number | null | undefined): string => {
    if (num === null || num === undefined) return '0';
    return num.toLocaleString('zh-CN');
  };

  // 格式化增长率
  const formatRate = (rate: string | null | undefined): { value: string; isPositive: boolean } => {
    if (!rate) return { value: '0%', isPositive: true };
    const numRate = parseFloat(rate);
    const isPositive = numRate >= 0;
    return { value: rate, isPositive };
  };

  // 表格列定义
  const columns: TableColumnProps[] = [
    {
      title: '日期',
      dataIndex: 'date',
      width: 150
    },
    {
      title: '非报单笔记数量',
      dataIndex: 'totalNum',
      width: 150,
      sorter: (a, b) => a.totalNum - b.totalNum,
      render: (text: number) => formatNumber(text)
    },
    {
      title: '推荐笔记数',
      dataIndex: 'recommendNum',
      width: 180,
      sorter: (a, b) => a.recommendNum - b.recommendNum,
      render: (text: number) => formatNumber(text)
    },
    {
      title: '话题笔记数',
      dataIndex: 'topicNum',
      width: 150,
      sorter: (a, b) => a.topicNum - b.topicNum,
      render: (text: number) => formatNumber(text)
    },
    {
      title: '报单笔记数',
      dataIndex: 'orderNum',
      width: 150,
      sorter: (a, b) => a.orderNum - b.orderNum,
      render: (text: number) => formatNumber(text)
    }
  ];

  // 处理日期选择
  const handleDateChange = (val: any) => {
    setQueryDate(val ? dayjs(val).format('YYYY-MM-DD') : '');
    setCurrentPage(1);
  };

  // 处理时间维度切换
  const handleTimeDimensionChange = (val: string) => {
    setTimeDimension(val);
    setCurrentPage(1);
  };

  // 处理查询
  const handleQuery = () => {
    setCurrentPage(1);
    fetchStatistics();
  };
  // 处理刷新
  const handleRefresh = () => {
    fetchStatistics();
  };
  // 处理重置
  const handleReset = () => {
    setTimeDimension('week');
    setQueryDate('');
    setCurrentPage(1);
  };
  // 表格数据（纯前端分页，按时间倒序）
  const tableData = useMemo(() => {
    if (!statisticsData?.daysDataList) return [];
    // 创建副本并按日期倒序排序（不修改原始数据）
    const sortedData = [...statisticsData.daysDataList].sort((a, b) => {
      // 日期格式为 'YYYY-MM-DD'，可以直接进行字符串比较
      return b.date.localeCompare(a.date);
    });
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    return sortedData.slice(start, end);
  }, [statisticsData, currentPage, pageSize]);

  // 图表容器引用
  const chartContainerRef = useRef<HTMLDivElement | null>(null);
  const chartInstanceRef = useRef<echarts.ECharts | null>(null);
  // 标记是否是首次渲染
  const isInitialMountRef = useRef(true);

  // 图表数据配置（转换 数据的 ECharts 格式）
  const chartDataTrans = (daysDataList: any[]) => {
    const dates: string[] = [];
    const totalNumData: number[] = [];
    const recommendNumData: number[] = [];
    const topicNumData: number[] = [];
    const orderNumData: number[] = [];

    daysDataList.forEach(item => {
      dates.push(item.date);
      totalNumData.push(item.totalNum);
      recommendNumData.push(item.recommendNum);
      topicNumData.push(item.topicNum);
      orderNumData.push(item.orderNum || 0);
    });

    return {
      dates,
      series: [
        {
          name: '非报单笔记数量',
          type: 'line',
          data: totalNumData,
          smooth: true,
          symbol: 'circle',
          symbolSize: 6
        },
        {
          name: '推荐笔记数',
          type: 'line',
          data: recommendNumData,
          smooth: true,
          symbol: 'circle',
          symbolSize: 6
        },
        {
          name: '话题笔记数',
          type: 'line',
          data: topicNumData,
          smooth: true,
          symbol: 'circle',
          symbolSize: 6
        },
        {
          name: '报单笔记数',
          type: 'line',
          data: orderNumData,
          smooth: true,
          symbol: 'circle',
          symbolSize: 6
        }
      ]
    };
  }


  return (
    <div className="note-statistics">
      {/* 顶部筛选区域 */}
      <div className="statistics-header">
        <Space size="large" wrap align="center">
          <div className="filter-item">
            <Text>时间维度：</Text>
            <RadioGroup
              options={timeDimensionOptions}
              type="button"
              value={timeDimension}
              onChange={handleTimeDimensionChange}
            />
          </div>
          <div className="filter-item">
            <Text>日期：</Text>
            <DatePicker
              value={queryDate ? dayjs(queryDate) : undefined}
              onChange={handleDateChange}
              style={{ width: 200 }}
              allowClear
            />
          </div>
          <div className="filter-item">
            <Button type="primary" onClick={handleQuery} loading={loading}>
              查询
            </Button>
            <Button type="outline"  onClick={handleRefresh} loading={loading}>
              刷新
            </Button>
            <Button type="outline" onClick={handleReset} loading={loading}>
              重置
            </Button>
          </div>
        </Space>
      </div>

      {/* 数据汇总卡片 */}
      <div className="statistics-cards">
        <Card className="statistics-card">
          <div className="card-content">
            <div className="card-left">
              <div className="card-value">{formatNumber(statisticsData?.noteTotalNum)}</div>
              <div className="card-label">非报单笔记数</div>
              <div className="card-rate">
                {statisticsData?.noteTotalNumPre != 0 && statisticsData?.noteTotalNumPre !== undefined ? (
                  statisticsData?.totalRate ? (() => {
                    const rate = formatRate(statisticsData.totalRate);
                    return (
                      <span className={rate.isPositive ? 'rate-positive' : 'rate-negative'}>
                        {rate.isPositive ? <Icon.IconArrowUp /> : <Icon.IconArrowDown />}
                        {rate.value} 较上{timeDimension === 'day' ? '日' : timeDimension === 'week' ? '周' : timeDimension === 'month' ? '月' : '年'} 同期
                      </span>
                    );
                  })() : null
                ) : (
                  <span style={{ color: '#86909c' }}>暂无上周期对比数据</span>
                )}
              </div>
            </div>
            <div className="card-icon">
              <Icon.IconFile />
            </div>
          </div>
        </Card>

        <Card className="statistics-card">
          <div className="card-content">
            <div className="card-left">
              <div className="card-value">{formatNumber(statisticsData?.noteRecommendNum)}</div>
              <div className="card-label">推荐笔记数</div>
              <div className="card-rate">
                {statisticsData?.noteRecommendNumPre != 0 && statisticsData?.noteRecommendNumPre !== undefined ? (
                  statisticsData?.recommendRate ? (() => {
                    const rate = formatRate(statisticsData.recommendRate);
                    return (
                      <span className={rate.isPositive ? 'rate-positive' : 'rate-negative'}>
                        {rate.isPositive ? <Icon.IconArrowUp /> : <Icon.IconArrowDown />}
                        {rate.value} 较上{timeDimension === 'day' ? '日' : timeDimension === 'week' ? '周' : timeDimension === 'month' ? '月' : '年'} 同期
                      </span>
                    );
                  })() : null
                ) : (
                  <span style={{ color: '#86909c' }}>暂无上周期对比数据</span>
                )}
              </div>
            </div>
            <div className="card-icon">
              <Icon.IconStar />
            </div>
          </div>
        </Card>

        <Card className="statistics-card">
          <div className="card-content">
            <div className="card-left">
              <div className="card-value">{formatNumber(statisticsData?.noteTopicNum)}</div>
              <div className="card-label">话题笔记数</div>
              <div className="card-rate">
                {statisticsData?.noteTopicNumPre != 0 && statisticsData?.noteTopicNumPre !== undefined ? (
                  statisticsData?.topicRate ? (() => {
                    const rate = formatRate(statisticsData.topicRate);
                    return (
                      <span className={rate.isPositive ? 'rate-positive' : 'rate-negative'}>
                        {rate.isPositive ? <Icon.IconArrowUp /> : <Icon.IconArrowDown />}
                        {rate.value} 较上{timeDimension === 'day' ? '日' : timeDimension === 'week' ? '周' : timeDimension === 'month' ? '月' : '年'} 同期
                      </span>
                    );
                  })() : null
                ) : (
                  <span style={{ color: '#86909c' }}>暂无上周期对比数据</span>
                )}
              </div>
            </div>
            <div className="card-icon">
              <Icon.IconTag />
            </div>
          </div>
        </Card>

        <Card className="statistics-card">
          <div className="card-content">
            <div className="card-left">
              <div className="card-value">{formatNumber(statisticsData?.noteOrderNum)}</div>
              <div className="card-label">报单笔记数</div>
              <div className="card-rate">
                {statisticsData?.noteOrderNumPre != 0 && statisticsData?.noteOrderNumPre !== undefined ? (
                  statisticsData?.orderRate ? (() => {
                    const rate = formatRate(statisticsData.orderRate);
                    return (
                      <span className={rate.isPositive ? 'rate-positive' : 'rate-negative'}>
                        {rate.isPositive ? <Icon.IconArrowUp /> : <Icon.IconArrowDown />}
                        {rate.value} 较上{timeDimension === 'day' ? '日' : timeDimension === 'week' ? '周' : timeDimension === 'month' ? '月' : '年'} 同期
                      </span>
                    );
                  })() : null
                ) : (
                  <span style={{ color: '#86909c' }}>暂无上周期对比数据</span>
                )}
              </div>
            </div>
            <div className="card-icon">
              <Icon.IconFile />
            </div>
          </div>
        </Card>
      </div>

      {/* 数据趋势分析图表 */}
      <Card className="chart-card" title="数据趋势分析" loading={loading}>
        {timeDimension === 'day' ? (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100px',
            color: '#86909c'
          }}>
            <Text>日维度不支持折线展示，请切换到周、月或年维度查看</Text>
          </div>
        ) : (
          <>
            <div className="chart-content">
              <div ref={chartContainerRef} style={{ width: '100%', height: '400px' }} />
            </div>
          </>
        )}
      </Card>

      {/* 详细数据表格 */}
      <Card className="table-card" title="详细数据" loading={loading}>
        <Table
          rowKey="date"
          columns={columns}
          data={tableData}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: statisticsData?.daysDataList?.length || 0,
            showTotal: true,
            sizeCanChange: true,
            sizeOptions: [20, 30, 50],
            onChange: (page, size) => {
              setCurrentPage(page);
              if (size && size !== pageSize) {
                setPageSize(size);
                setCurrentPage(1);
              }
            }
          }}
        />
      </Card>
    </div>
  );
}

export default NoteStatistics;
